/**
 * Client-side exports (client-only pivot, Phase 5).
 *
 * Export is the ONLY way content leaves the tab — nothing is stored
 * anywhere. Notes render from the TipTap document via the surviving
 * block pipeline (notes-markdown-blocks); the DOCX paragraph mapping is
 * ported unchanged from the pre-pivot server exporter.
 */
import {
  AlignmentType,
  Document,
  ExternalHyperlink,
  HeadingLevel,
  Packer,
  Paragraph,
  TextRun,
  type IRunOptions,
  type ParagraphChild,
} from "docx";
import {
  notesDocumentToMarkdownBlocks,
  type MarkdownBlock,
} from "@/lib/exports/notes-markdown-blocks";
import { notesDocumentToHtml } from "@/lib/notes/tiptap-document";
import { sanitizeRenderedNotesHtml } from "@/lib/notes/sanitize-rendered-html";
import type { TranscriptSegment } from "@/lib/types/domain";

// ── Notes → Markdown ───────────────────────────────────────────────────────

/**
 * Ordered-list numbering over the flat item sequence `flattenListItems`
 * produces. Counters live per nesting level: a nested bullet run must not
 * reset its parent's numbering (the Open Questions shape every generated
 * note has — numbered question, bulleted context), and leaving a level
 * restarts that level's count. A fresh level seeds from the item's list
 * `start` offset so offset lists keep their numbering in MD/DOCX just as
 * the HTML/PDF path's `<ol start>` does.
 */
function createListNumbering(): (item: {
  ordered: boolean;
  indent: number;
  start?: number;
}) => number {
  const ordinals: number[] = [];
  return (item) => {
    ordinals.length = item.indent + 1;
    ordinals[item.indent] = item.ordered
      ? (ordinals[item.indent] ?? (item.start ?? 1) - 1) + 1
      : 0;
    return ordinals[item.indent];
  };
}

export function markdownBlocksToString(
  blocks: readonly MarkdownBlock[],
): string {
  const parts: string[] = [];
  for (const block of blocks) {
    switch (block.kind) {
      case "heading":
        parts.push(`${"#".repeat(block.level)} ${block.text}`);
        break;
      case "paragraph":
        parts.push(block.text);
        break;
      case "blockquote":
        parts.push(
          block.text
            .split("\n")
            .map((line) => `> ${line}`)
            .join("\n"),
        );
        break;
      case "code":
        parts.push(`\`\`\`${block.language ?? ""}\n${block.text}\n\`\`\``);
        break;
      case "rule":
        parts.push("---");
        break;
      case "list": {
        const nextOrdinal = createListNumbering();
        parts.push(
          block.items
            .map((item) => {
              const ordinal = nextOrdinal(item);
              // 4 spaces per level: enough for CommonMark to nest under an
              // ordered parent ("1. " is a 3-column marker; 2 would fall out
              // of the item), still short of the +4 indented-code threshold.
              const indent = "    ".repeat(item.indent);
              const marker = item.ordered ? `${ordinal}.` : "-";
              const checkbox =
                item.checked == null ? "" : item.checked ? "[x] " : "[ ] ";
              return `${indent}${marker} ${checkbox}${item.text}`;
            })
            .join("\n"),
        );
        break;
      }
    }
  }
  return `${parts.join("\n\n")}\n`;
}

export function notesToMarkdown(doc: unknown): string {
  return markdownBlocksToString(notesDocumentToMarkdownBlocks(doc));
}

// ── Notes → DOCX ───────────────────────────────────────────────────────────

/**
 * Raw-text path (code blocks, whose content must render verbatim). OOXML
 * only breaks lines on an explicit `<w:br/>` — a raw "\n" inside a run
 * renders as plain whitespace in Word — so each line becomes its own run
 * with `break: 1` on the continuations.
 */
function textToRuns(
  text: string,
  options: Omit<IRunOptions, "text" | "break" | "children"> = {},
): TextRun[] {
  return text
    .split("\n")
    .map(
      (line, index) =>
        new TextRun({ ...options, text: line, ...(index > 0 ? { break: 1 } : {}) }),
    );
}

// The characters the emitter backslash-escapes in regular inline text.
const INLINE_ESCAPABLE = new Set(["\\", "`", "*", "~", "[", "]"]);

/**
 * Locate the `](` + `)` pair closing a link opened just before `labelStart`.
 * The label may legitimately contain `]` — escaped by the emitter outside
 * code spans, verbatim inside them — so honor both while searching.
 */
function findLinkBounds(
  text: string,
  labelStart: number,
): { labelEnd: number; end: number } | null {
  let index = labelStart;
  let fence = 0;
  while (index < text.length) {
    const char = text[index];
    if (char === "`") {
      let run = 1;
      while (text[index + run] === "`") run += 1;
      if (fence === 0) fence = run;
      else if (run === fence) fence = 0;
      index += run;
      continue;
    }
    if (fence > 0) {
      index += 1;
      continue;
    }
    // Same escape rule as the main scanner, so both agree on every position.
    if (char === "\\" && INLINE_ESCAPABLE.has(text[index + 1] ?? "")) {
      index += 2;
      continue;
    }
    if (char === "]") {
      if (text[index + 1] !== "(") return null;
      const end = text.indexOf(")", index + 2);
      return end >= 0 ? { labelEnd: index, end } : null;
    }
    index += 1;
  }
  return null;
}

/**
 * Inline block text arrives from the notes-markdown-blocks collector already
 * carrying markdown syntax: backslash escapes, `**`/`*`/`~~` style
 * delimiters, backtick code fences (verbatim content), and `[label](href)`
 * links. Word must never show those delimiters, so this scans that closed
 * grammar back into styled runs; links become real `ExternalHyperlink`s so
 * the href survives outside the tab. Same `\n` → `break: 1` contract as
 * `textToRuns`.
 */
function inlineTextToRuns(
  text: string,
  options: Omit<IRunOptions, "text" | "break" | "children"> = {},
): ParagraphChild[] {
  const children: ParagraphChild[] = [];
  const style = { bold: false, italics: false, strike: false };
  let codeFence = 0;
  let link: { href: string; labelEnd: number; end: number } | null = null;
  let linkRuns: TextRun[] = [];
  let buffer = "";
  let pendingBreak = false;

  const flush = () => {
    if (!buffer && !pendingBreak) return;
    const run = new TextRun({
      ...options,
      text: buffer,
      ...(pendingBreak ? { break: 1 } : {}),
      ...(style.bold ? { bold: true } : {}),
      ...(style.italics ? { italics: true } : {}),
      ...(style.strike ? { strike: true } : {}),
      ...(codeFence > 0 ? { font: "Courier New" } : {}),
      ...(link ? { style: "Hyperlink" } : {}),
    });
    (link ? linkRuns : children).push(run);
    buffer = "";
    pendingBreak = false;
  };

  let index = 0;
  while (index < text.length) {
    if (link && index === link.labelEnd) {
      flush();
      children.push(
        new ExternalHyperlink({
          // An empty label still has to surface the destination somewhere.
          children:
            linkRuns.length > 0
              ? linkRuns
              : [new TextRun({ ...options, text: link.href, style: "Hyperlink" })],
          link: link.href,
        }),
      );
      linkRuns = [];
      index = link.end + 1;
      link = null;
      continue;
    }
    const char = text[index];
    if (char === "\n") {
      flush();
      pendingBreak = true;
      index += 1;
      continue;
    }
    if (codeFence > 0) {
      // Code content is verbatim: only the matching closing fence ends it.
      if (char === "`") {
        let run = 1;
        while (text[index + run] === "`") run += 1;
        if (run === codeFence) {
          // Undo the emitter's protective one-space padding.
          if (buffer.startsWith(" ") && buffer.endsWith(" ") && buffer.trim()) {
            buffer = buffer.slice(1, -1);
          }
          flush();
          codeFence = 0;
        } else {
          buffer += text.slice(index, index + run);
        }
        index += run;
        continue;
      }
      buffer += char;
      index += 1;
      continue;
    }
    if (char === "\\" && INLINE_ESCAPABLE.has(text[index + 1] ?? "")) {
      buffer += text[index + 1];
      index += 2;
      continue;
    }
    if (text.startsWith("**", index)) {
      flush();
      style.bold = !style.bold;
      index += 2;
      continue;
    }
    if (char === "*") {
      flush();
      style.italics = !style.italics;
      index += 1;
      continue;
    }
    if (text.startsWith("~~", index)) {
      flush();
      style.strike = !style.strike;
      index += 2;
      continue;
    }
    if (char === "`") {
      let run = 1;
      while (text[index + run] === "`") run += 1;
      flush();
      codeFence = run;
      index += run;
      continue;
    }
    if (char === "[" && !link) {
      const bounds = findLinkBounds(text, index + 1);
      if (bounds) {
        flush();
        link = { href: text.slice(bounds.labelEnd + 2, bounds.end), ...bounds };
        index += 1;
        continue;
      }
    }
    buffer += char;
    index += 1;
  }
  flush();
  return children;
}

function headingLevel(level: 1 | 2 | 3 | 4) {
  switch (level) {
    case 1:
      return HeadingLevel.HEADING_1;
    case 2:
      return HeadingLevel.HEADING_2;
    case 3:
      return HeadingLevel.HEADING_3;
    default:
      return HeadingLevel.HEADING_4;
  }
}

function blocksToParagraphs(blocks: readonly MarkdownBlock[]): Paragraph[] {
  const paragraphs: Paragraph[] = [];

  for (const block of blocks) {
    switch (block.kind) {
      case "heading":
        paragraphs.push(
          new Paragraph({
            children: inlineTextToRuns(block.text),
            heading: headingLevel(block.level),
            spacing: { before: 320, after: 120 },
          }),
        );
        break;
      case "paragraph":
        paragraphs.push(
          new Paragraph({
            children: inlineTextToRuns(block.text),
            spacing: { after: 180 },
          }),
        );
        break;
      case "blockquote":
        paragraphs.push(
          new Paragraph({
            children: inlineTextToRuns(block.text, { italics: true }),
            border: {
              left: { color: "94A3B8", size: 6, space: 12, style: "single" },
            },
            indent: { left: 320 },
            spacing: { after: 180 },
          }),
        );
        break;
      case "code":
        paragraphs.push(
          new Paragraph({
            children: textToRuns(block.text, { font: "Courier New" }),
            shading: { fill: "F8FAFC" },
            spacing: { after: 180 },
          }),
        );
        break;
      case "rule":
        paragraphs.push(
          new Paragraph({
            children: [new TextRun({ text: "────────────────────────" })],
            alignment: AlignmentType.CENTER,
            spacing: { before: 120, after: 120 },
          }),
        );
        break;
      case "list": {
        const nextOrdinal = createListNumbering();
        block.items.forEach((item) => {
          const ordinal = nextOrdinal(item);
          const prefix =
            item.checked == null
              ? item.ordered
                ? `${ordinal}. `
                : "• "
              : item.checked
                ? "☑ "
                : "☐ ";

          paragraphs.push(
            new Paragraph({
              children: inlineTextToRuns(`${prefix}${item.text}`),
              indent: { left: 320 + item.indent * 280 },
              spacing: { after: 80 },
            }),
          );
        });
        paragraphs.push(new Paragraph({ text: "", spacing: { after: 60 } }));
        break;
      }
    }
  }

  return paragraphs;
}

/** @internal Exported for tests — the Document that notesToDocxBlob packs. */
export function notesToDocxDocument(doc: unknown): Document {
  const blocks = notesDocumentToMarkdownBlocks(doc);
  return new Document({
    sections: [{ children: blocksToParagraphs(blocks) }],
  });
}

export async function notesToDocxBlob(doc: unknown): Promise<Blob> {
  return Packer.toBlob(notesToDocxDocument(doc));
}

// ── Notes → print (the v1 PDF path) ───────────────────────────────────────

/**
 * Print the notes via a hidden same-origin iframe (`srcdoc`); the user
 * saves as PDF from the browser's print dialog. No popup (immune to
 * blockers) and no `document.write`. HTML goes through the same
 * normalize + DOMPurify wall as on-screen rendering.
 *
 * Settles only once the frame has loaded and `print()` has returned —
 * the caller disarms the unload guard on resolution, so resolving any
 * earlier (e.g. when the frame is merely appended) would drop the
 * data-loss warning before a dialog even exists. No browser reports
 * whether the user printed or cancelled the dialog, so "dialog opened"
 * is the closest observable point to "content left the tab"; rejects
 * when the frame yields no window to print from.
 */
export function printNotes(doc: unknown): Promise<void> {
  const html = sanitizeRenderedNotesHtml(notesDocumentToHtml(doc));
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.srcdoc = `<!doctype html>
<html><head><title>wendai notes</title><style>
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
         max-width: 46rem; margin: 2rem auto; padding: 0 1rem; line-height: 1.6; }
  pre { background: #f8fafc; padding: .75rem; overflow-x: auto; }
  blockquote { border-left: 3px solid #94a3b8; margin-left: 0; padding-left: 1rem; color: #475569; }
</style></head><body>${html}</body></html>`;

  return new Promise((resolve, reject) => {
    iframe.onload = () => {
      // A stray second load event must not re-open the print dialog.
      iframe.onload = null;
      const frameWindow = iframe.contentWindow;
      if (!frameWindow) {
        iframe.remove();
        reject(new Error("Print frame has no window"));
        return;
      }
      try {
        frameWindow.focus();
        frameWindow.print();
      } catch (error) {
        // print() can throw (blocked printing, embedded webviews) — the
        // promise must settle regardless or the caller awaits forever.
        iframe.remove();
        reject(error);
        return;
      }
      // Chrome blocks inside print(); Safari/Firefox return immediately —
      // keep the frame alive long enough for the dialog to grab the content.
      setTimeout(() => iframe.remove(), 60_000);
      resolve();
    };

    document.body.appendChild(iframe);
  });
}

// ── Transcript exports ─────────────────────────────────────────────────────

function formatClock(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

interface TranscriptExportOptions {
  /** Prefix each line with its start time. SRT ignores this — the format IS timing. */
  includeTimestamps?: boolean;
}

export function transcriptToText(
  segments: TranscriptSegment[],
  { includeTimestamps = true }: TranscriptExportOptions = {},
): string {
  const lines = segments.flatMap((segment) => {
    // One line per segment: editor-inserted newlines flatten to spaces, and
    // a segment cleared in the editor contributes no line at all.
    const text = segment.text.replace(/\s*\n\s*/g, " ").trim();
    if (!text) return [];
    const time =
      includeTimestamps && segment.startMs !== null
        ? `[${formatClock(segment.startMs)}] `
        : "";
    return [`${time}${text}`];
  });
  return `${lines.join("\n")}\n`;
}

export function transcriptToMarkdown(
  segments: TranscriptSegment[],
  { includeTimestamps = true }: TranscriptExportOptions = {},
): string {
  return `# Transcript\n\n${segments
    .map((segment) => {
      const time =
        includeTimestamps && segment.startMs !== null
          ? `**${formatClock(segment.startMs)}** `
          : "";
      return `${time}${segment.text}`;
    })
    .join("\n\n")}\n`;
}

function formatSrtTime(ms: number): string {
  const clock = formatClock(ms);
  const millis = String(ms % 1000).padStart(3, "0");
  return `${clock},${millis}`;
}

export function transcriptToSrt(segments: TranscriptSegment[]): string {
  let cursorMs = 0;
  const cues: string[] = [];
  for (const segment of segments) {
    const start = segment.startMs ?? cursorMs;
    const end = segment.endMs ?? start;
    cursorMs = end;
    // A blank line TERMINATES an SRT cue, so editor-inserted blank lines —
    // including whitespace-only ones — must go; the single newlines that
    // remain are legal multi-line cues.
    const text = segment.text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line !== "")
      .join("\n");
    // A cue with an empty body would read as a blank line too: skip it and
    // keep the SRT indices sequential.
    if (!text) continue;
    cues.push(`${cues.length + 1}\n${formatSrtTime(start)} --> ${formatSrtTime(end)}\n${text}`);
  }
  return `${cues.join("\n\n")}\n`;
}

/** Filename-safe base derived from the audio filename. */
export function exportBaseName(audioFileName: string | undefined): string {
  const stem = (audioFileName ?? "").replace(/\.[^.]+$/, "").trim();
  const safe = stem.replace(/[^\p{L}\p{N}\-_ ]/gu, "").replace(/\s+/g, "-");
  return safe || "wendai";
}

// ── Download helper ────────────────────────────────────────────────────────

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

export function downloadText(text: string, filename: string): void {
  downloadBlob(new Blob([text], { type: "text/plain;charset=utf-8" }), filename);
}
